import api from "./api";

/**
 * 💬 CHAT SERVICE
 * Handles all chat-related API calls for conversations and messages
 */
export const chatService = {
  /**
   * 📋 Get conversations list with pagination and filters
   */
  getConversations: async (params = {}) => {
    try {
      const response = await api.get("/conversations", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      throw error;
    }
  },

  /**
   * 📊 Get conversations summary/stats
   */
  getSummary: async () => {
    try {
      const response = await api.get("/conversations/summary");
      return response.data;
    } catch (error) {
      console.error("Error fetching conversations summary:", error);
      throw error;
    }
  },

  /**
   * 🚨 Get escalated conversations
   */
  getEscalated: async () => {
    try {
      const response = await api.get("/conversations/escalated");
      return response.data;
    } catch (error) {
      console.error("Error fetching escalated conversations:", error);
      throw error;
    }
  },

  /**
   * 🔍 Get specific conversation details
   */
  getConversation: async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching conversation:", error);
      throw error;
    }
  },

  /**
   * 💬 Get messages for a conversation
   */
  getMessages: async (conversationId, params = {}) => {
    try {
      const response = await api.get(
        `/conversations/${conversationId}/messages`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  },

  /**
   * 📤 Send message as operator
   */
  sendMessage: async (conversationId, data) => {
    try {
      const response = await api.post(
        `/conversations/${conversationId}/messages`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  },

  /**
   * 📷 Upload image file
   */
  uploadImage: async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append("image", imageFile);

      const response = await api.post("/conversations/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  },

  /**
   * 💰 Get conversation cost tracking
   */
  getCost: async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/cost`);
      return response.data;
    } catch (error) {
      console.error("Error fetching conversation cost:", error);
      throw error;
    }
  },

  /**
   * 📎 Get media files for conversation
   */
  getMedia: async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}/media`);
      return response.data;
    } catch (error) {
      console.error("Error fetching conversation media:", error);
      throw error;
    }
  },

  /**
   * 📖 Mark conversation as read
   */
  markAsRead: async (conversationId, operatorName) => {
    try {
      const response = await api.put(
        `/conversations/${conversationId}/mark-read`,
        { operatorName }
      );
      return response.data;
    } catch (error) {
      console.error("Error marking conversation as read:", error);
      throw error;
    }
  },
};

/**
 * 🎯 HELPER FUNCTIONS FOR DATA TRANSFORMATION
 */

/**
 * Transform backend conversation to frontend format
 */
export const transformConversation = (backendConv) => {
  return {
    id: backendConv.id,
    channel_id: backendConv.channel_id,
    customer_id: backendConv.customer_id,
    customer_name: backendConv.customer_name,
    status: backendConv.status,
    created_at: backendConv.created_at,
    updated_at: backendConv.updated_at,

    // Channel info
    channel_name: backendConv.channel_name,
    channel_type: backendConv.channel_type,
    agent_name: backendConv.agent_name,

    // Assignment info (map backend field to frontend)
    assigned_to: backendConv.assigned_operator,
    escalation_reason: backendConv.escalation_reason,
    escalated_at: backendConv.escalated_at,
    resolved_at: backendConv.resolved_at,

    // Message info
    last_message: backendConv.last_message,
    last_message_at: backendConv.last_message_at,
    last_message_media_type: backendConv.last_message_media_type || "text",
    has_media_files: backendConv.has_media_files || false,
    message_count: backendConv.message_count || 0,

    // Model info
    model_provider: backendConv.model_provider,
    model_name: backendConv.model_name,

    // Frontend-specific fields
    unread_count:
      backendConv.unread_count !== undefined &&
      backendConv.unread_count !== null
        ? Number(backendConv.unread_count)
        : 0,
    is_online: Math.random() > 0.5, // TODO: Add real online status from backend
    is_pinned: false, // TODO: Add pinning feature to backend
    customer_avatar: null, // TODO: Add avatar support to backend
  };
};

/**
 * Transform backend message to frontend format
 */
export const transformMessage = (backendMsg) => {
  return {
    id: backendMsg.id,
    conversation_id: backendMsg.conversation_id,
    role: backendMsg.role,
    content: backendMsg.content,
    media_type: backendMsg.media_type,
    media_path: backendMsg.media_path,
    created_at: backendMsg.created_at,
    operator_name: backendMsg.operator_name,
    cost: backendMsg.cost,
    has_media: backendMsg.has_media || false,
    media_url: backendMsg.media_url,
  };
};

/**
 * Get color class for conversation status
 * @param {string} status - The conversation status
 * @returns {string} Tailwind CSS color classes
 */
export const getStatusColor = (status) => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "escalated":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    case "assigned":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "resolved":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    case "disable":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    case "pending":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }
};

export default chatService;
